import { useCallback, useEffect, useState } from "react";
import { Button, Pagination, Table } from "antd";
import paymentApi from "../../../api/paymentApi";
import Modal from "../../../components/Modal";
import UpdatePaymentRequest from "./UpdatePaymentRequest";
import CancellPayment from "./CancellPayment";
import FilterCom from "./Filter";
import { getWithdrawColumns } from "./withdraw-table-columns";
import PaymentSummaryBar from "../Payment/payment-summary-bar";
import ExportCsvButton from "../../../components/export-csv-button";
import WithdrawLimitModal from "./withdraw-limit-modal";
import MonthlyTaxReportModal from "../../../components/monthly-tax-report-modal";

// Nhãn báo cáo thuế phần rút tiền: chỉ lệnh ĐÃ chuyển khoản, gom theo ngày xử lý
const WITHDRAW_TAX_LABELS = {
    users: "Số user rút",
    count: "Số lệnh rút",
    totalAmount: "Tổng tiền đã chuyển (đ)",
    detailTooltip: "Mỗi lệnh rút đã chuyển khoản 1 dòng (cùng cột với nút Xuất CSV)",
};

export interface Payment {
    paymentId: number;
    amount: number;
    createTime: number;
    completeTime?: number;
    status: number;
    paymentType?: string;
    paymentImage?: string;
    bankAccountName: string;
    bankAccountNumber: string;
    userId: number;
    adminProcessId?: number;
    bankId: number;
    Bank?: { bankId: number; bankName: string; bin: number };
    user: { name: string; userId: number };
    admin?: { name: string; userId: number };
    note: string;
    totalAmountWithdraw?: number;
    totalCommission?: number;
    bonus?: number;
}

export type Filter = {
    status?: number;
    page: number;
    limit: number;
    startDate?: number;
    endDate?: number;
    userId?: number;
    minAmount?: number;
};

function buildQueryString(filter: Filter): string {
    const parts = Object.entries(filter).reduce((acc: string[], [key, val]) => {
        if (!val) return acc;
        if (key === "startDate") acc.push(`createTime[gte]=${val}`);
        else if (key === "endDate") acc.push(`createTime[lte]=${val}`);
        else if (key === "minAmount") acc.push(`amount[gte]=${val}`);
        else acc.push(`${key}=${val}`);
        return acc;
    }, []);
    parts.push("sort=-createTime"); // newest first
    return parts.join("&");
}

const Withdraw = () => {
    const [payments, setPayments] = useState<Payment[]>([]);
    const [total, setTotal] = useState(0);
    const [statusCounts, setStatusCounts] = useState({ pending: 0, completed: 0, cancelled: 0 });
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [openLimitModal, setOpenLimitModal] = useState(false);
    const [openTaxReport, setOpenTaxReport] = useState(false);
    const [paymentUpdate, setPaymentUpdate] = useState<{
        type: "complete" | "cancell";
        payment: Payment;
    }>();
    const [filter, setFilter] = useState<Filter>({
        limit: 10,
        page: 1,
        status: undefined,
        startDate: undefined,
        endDate: undefined,
        userId: undefined,
    });

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await paymentApi.getAll(buildQueryString(filter));
            setPayments(data.data.payments);
            setTotal(data.data.totalPayment);
            if (data.data.statusCounts) setStatusCounts(data.data.statusCounts);
        } catch (err) {
            console.error("[Withdraw] fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    const handleOpenUpdatePayment = (payment: Payment, type: "complete" | "cancell") => {
        setOpenModal(true);
        setPaymentUpdate({ type, payment });
    };

    const handleFilterByUser = (userId: string) =>
        setFilter((prev) => ({ ...prev, userId: Number(userId), page: 1 }));

    const onChangePage = (page: number, limit: number) =>
        setFilter((prev) => ({ ...prev, page, limit }));

    const dataSource = payments.map((el) => ({ ...el, key: el.paymentId }));

    return (
        <div className="p-4 flex-1 min-w-0 overflow-auto flex flex-col gap-3 bg-surface-sunken min-h-full">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="text-xl font-bold text-content m-0">Quản lý Yêu cầu rút tiền</h1>
                <div className="flex items-center gap-2">
                    <Button onClick={() => setOpenLimitModal(true)}>Hạn mức rút</Button>
                    <Button onClick={() => setOpenTaxReport(true)}>Báo cáo thuế</Button>
                    <ExportCsvButton
                        filename="yeu-cau-rut-tien"
                        disabled={!total}
                        tooltip="Tải toàn bộ yêu cầu đang lọc ra CSV (mọi trang) để tự cộng tổng tiền đã chi / đã huỷ và đối chiếu với số phía dưới"
                        request={() => paymentApi.exportCsv(buildQueryString(filter))}
                    />
                </div>
            </div>
            <FilterCom filter={filter} setFilter={setFilter} />
            <PaymentSummaryBar statusCounts={statusCounts} total={total} />
            <div className="w-full overflow-auto bg-surface rounded-lg shadow-sm">
                <Table
                    dataSource={dataSource}
                    columns={getWithdrawColumns(handleOpenUpdatePayment, handleFilterByUser)}
                    pagination={false}
                    loading={loading}
                    size="small"
                    scroll={{ x: 1450 }}
                />
            </div>
            <div className="flex justify-end">
                <Pagination
                    total={total}
                    pageSize={filter.limit}
                    current={filter.page}
                    pageSizeOptions={[10, 20, 50, 100, 500]}
                    showSizeChanger
                    onChange={onChangePage}
                    showTotal={(t) => `Tổng: ${t}`}
                />
            </div>
            <Modal isOpen={openModal}>
                {paymentUpdate?.type === "complete" && (
                    <UpdatePaymentRequest
                        setOpenModal={setOpenModal}
                        payment={paymentUpdate.payment}
                        setPayment={setPayments}
                        fetchPayment={fetchPayments}
                    />
                )}
                {paymentUpdate?.type === "cancell" && (
                    <CancellPayment
                        setPayment={setPayments}
                        setOpenModal={setOpenModal}
                        payment={paymentUpdate.payment}
                        fetchPayment={fetchPayments}
                    />
                )}
            </Modal>
            <WithdrawLimitModal open={openLimitModal} onClose={() => setOpenLimitModal(false)} />
            <MonthlyTaxReportModal
                open={openTaxReport}
                onClose={() => setOpenTaxReport(false)}
                title="Báo cáo rút tiền theo tháng (khai thuế)"
                filePrefix="rut-tien"
                labels={WITHDRAW_TAX_LABELS}
                note="Chỉ tính lệnh đã chuyển khoản (Đã xử lý), theo ngày xử lý; lệnh chờ duyệt / đã huỷ không tính."
                fetchSummary={paymentApi.getMonthlyReport}
                exportCsv={paymentApi.exportMonthlyReport}
            />
        </div>
    );
};

export default Withdraw;
